import type { SeatKind, SeatingMove, SeatingState } from '@peles/shared';

/** A party's place in an arrangement: its seats at a table, or how many. */
export interface SeatingPlacement {
  guestId: string;
  tableUid: string;
  seats?: number[];
  count?: number;
  /** Who sits in each of `seats`: a named member's id, or null for an unnamed seat. */
  named?: (string | null)[];
  /** Who each of `seats` is for — a man, a woman or a child — when seated by section. */
  kinds?: (SeatKind | null)[];
  /**
   * On the board only, never sent: the chair a producer chose for the party.
   * The party takes it and the free seats after it.
   */
  startSeat?: number;
}

/** A whole arrangement: who gives up their seats, and where everyone goes. */
export interface SeatingArrangement {
  reseat: string[];
  placements: SeatingPlacement[];
}
import { apiClient } from './client';

/**
 * `/events/:eventId/seating` — who sits where on one of an event's floor
 * plans, seat by seat, and the changes to it. Every answer is the plan's
 * seating as it now stands.
 */

const base = (eventId: string) => `/events/${eventId}/seating`;

export async function getSeating(eventId: string, floorPlanId?: string): Promise<SeatingState> {
  const { data } = await apiClient.get<SeatingState>(base(eventId), {
    params: floorPlanId ? { floorPlanId } : undefined,
  });
  return data;
}

export async function moveSeats(
  eventId: string,
  body: { floorPlanId?: string; moves: SeatingMove[]; allowOverCapacity?: boolean },
): Promise<SeatingState> {
  const { data } = await apiClient.post<SeatingState>(`${base(eventId)}/moves`, body);
  return data;
}

export async function lockSeating(
  eventId: string,
  floorPlanId: string,
  locked: boolean,
): Promise<SeatingState> {
  const { data } = await apiClient.post<SeatingState>(`${base(eventId)}/lock`, {
    floorPlanId,
    locked,
  });
  return data;
}

export async function setPrimarySeating(
  eventId: string,
  floorPlanId: string,
): Promise<SeatingState> {
  const { data } = await apiClient.post<SeatingState>(`${base(eventId)}/primary`, {
    floorPlanId,
  });
  return data;
}

export async function arrangeSeats(
  eventId: string,
  floorPlanId: string,
  arrangement: SeatingArrangement,
): Promise<SeatingState> {
  const { data } = await apiClient.post<SeatingState>(`${base(eventId)}/arrangement`, {
    floorPlanId,
    ...arrangement,
  });
  return data;
}

export async function pinSeats(
  eventId: string,
  floorPlanId: string,
  guestIds: string[],
  locked: boolean,
): Promise<SeatingState> {
  const { data } = await apiClient.post<SeatingState>(`${base(eventId)}/pin`, {
    floorPlanId,
    guestIds,
    locked,
  });
  return data;
}
